import { useState, useEffect, useCallback } from "react";
import { ContractorApi } from "api/contractors/service";
import { UpgradeWorkOrderDialog } from "./UpgradeWorkOrderDialog";
import {
  ArrowLeft,
  Download,
  ArrowUpRight,
  Calendar,
  FileText,
  IndianRupee,
  Building2,
  User,
  Clock,
  CheckCircle2,
  AlertCircle,
  Shield,
  ChevronDown,
  ChevronUp,
  Paperclip,
  History,
  Pencil,
  Trash,
  X,
  Plus,
  Trash2,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { PDFPreviewModal } from "./PDFPreviewModal";
import { createPortal } from "react-dom";

import { EditMilestonesModal } from "./EditMilestonesModal";
import ConfirmationModal from "components/ConfirmationModal";

// Detailed work order details will be fetched from API

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Select = ({ className = "", options = [], ...props }) => (
  <select
    className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-2 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

const Badge = ({ variant = "none", className = "", children }) => {
  const baseStyles =
    "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
  const variants = {
    none: "",
    default:
      "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
    secondary:
      "border-transparent bg-sm-secondary text-sm-secondary-foreground hover:bg-sm-secondary/80",
    destructive:
      "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
    outline: "text-foreground",
  };
  return (
    <div className={`${baseStyles} ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

const Card = ({ className = "", children }) => (
  <div
    className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}
  >
    {children}
  </div>
);

const CardHeader = ({ className = "", children }) => (
  <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
);

const CardTitle = ({ className = "", children }) => (
  <h3
    className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
  >
    {children}
  </h3>
);

const CardContent = ({ className = "", children }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const Separator = ({ className = "" }) => (
  <div className={`shrink-0 bg-border h-[1px] w-full ${className}`} />
);

const roundToTwo = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.round(numericValue * 100) / 100;
};

const calculatePercentage = (amount, total) => {
  const numericTotal = Number(total);
  if (!numericTotal) return 0;
  return roundToTwo(((Number(amount) || 0) / numericTotal) * 100);
};

export function WorkOrderDetailView({
  workOrder,
  contractor,
  onBack,
  onDelete,
  onUpgrade,
  onRefresh,
}) {
  const [expandedVersions, setExpandedVersions] = useState([]);
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditMilestonesModal, setShowEditMilestonesModal] = useState(false);
  const [showEditTermsModal, setShowEditTermsModal] = useState(false);
  const [editTermsData, setEditTermsData] = useState({
    terms: "",
    paymentTerms: "",
  });

  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [paymentStructure, setPaymentStructure] = useState("");
  const [structureMilestones, setStructureMilestones] = useState([
    { id: 1, name: "", description: "", percentage: 100, amount: 0 },
  ]);
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [organization, setOrganization] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingWorkOrder, setIsDeletingWorkOrder] = useState(false);

  // Editing State
  const [editingScopeId, setEditingScopeId] = useState(null);
  const [scopeEdits, setScopeEdits] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Constants
  const UNIT_OPTIONS = [
    "custom",
    "nos",
    "sq_m",
    "sq_ft",
    "mtrs",
    "ft",
    "inches",
  ];

  useEffect(() => {
    try {
      const org = localStorage.getItem("organization");
      if (org) setOrganization(JSON.parse(org));
    } catch (err) {
      console.error("Error parsing organization from localStorage:", err);
    }
  }, []);

  const fetchDetails = useCallback(async () => {
    if (!workOrder?.workorder_id) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await ContractorApi.getWorkOrderDetail(
        workOrder.workorder_id,
      );
      if (response.status) {
        const apiData = response.data;
        const versionCount = apiData.version_timeline?.length || 0;
        const startDate =
          apiData.version_timeline?.[versionCount - 1]?.start_date;
        const endDate = apiData.version_timeline?.[0]?.end_date;
        const duration = differenceInDays(
          new Date(endDate),
          new Date(startDate),
        );

        const mappedDetails = {
          ...workOrder,
          id: apiData.id,
          status: apiData.status,
          projectName: apiData.site_name,
          contractType: apiData.contract_type,
          paymentStructure: apiData.payment_structure,
          startDate,
          endDate,
          duration,
          value: apiData.financial_summary?.total_value || 0,
          paidAmount: apiData.financial_summary?.total_paid || 0,
          approvalStatus: apiData.status,
          scopeItems: (apiData.scope_of_work || []).map((item) => ({
            id: item.id,
            item: item.item_name,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unit_cost,
            totalCost: item.total_price,
          })),
          milestones: (apiData.milestones || []).map((m) => ({
            id: m.id,
            name: m.name,
            description: m.description,
            percentage: calculatePercentage(
              m.amount,
              apiData.financial_summary?.total_value,
            ),
            value: m.amount,
            paidAmount: m.paid_so_far,
            status:
              m.paid_so_far >= m.amount && m.amount > 0
                ? "Paid"
                : m.paid_so_far > 0
                  ? "In Progress"
                  : "Pending",
          })),
          monthlyBills: apiData.monthly_bills,
          // CHANGED: added startDate and endDate to each version
          versions: (apiData.version_timeline || []).map((v) => ({
            version_id: v.id,
            version: v.version_number,
            updatedOn: v.created_on,
            startDate: v.start_date,
            endDate: v.end_date,
            totalValue: v.total_value,
            updatedBy: v.updated_by,
            scopeItems: (v.scope_items || []).map((item) => ({
              id: item.id,
              item: item.item_name,
              description: item.description,
              totalCost: item.total_price,
            })),
            milestones: (v.milestones || []).map((m) => ({
              id: m.id,
              name: m.name,
              description: m.description,
              percentage: calculatePercentage(m.amount, v.total_value),
            })),
            terms: v.terms_and_conditions || "",
            paymentTerms: v.payment_terms || "",
            acknowledgmentDate: v.created_on, // Fallback as API doesn't have specific acknowledgment_date
          })),
          terms:
            apiData.version_timeline?.[0]?.terms_and_conditions ||
            "Standard contract terms apply.",
          acknowledgmentDate: apiData.date_of_issue,
          attachments: apiData.attachments || [],
          paymentTerms: apiData.version_timeline?.[0]?.payment_terms || "",
          retentionPercent: 0, // Not in API
          scope_notes: apiData.scope_notes || "",
          scopeNotes: apiData.scope_notes || "", // kept for older UI usage
        };
        setDetails(mappedDetails);
      } else {
        setError(response.message || "Failed to fetch work order details");
      }
    } catch (err) {
      console.error("Error fetching work order details:", err);
      setError("An error occurred while fetching details");
    } finally {
      setIsLoading(false);
    }
  }, [workOrder?.workorder_id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount) || 0;
    if (numericAmount >= 100000)
      return `₹${(numericAmount / 100000).toFixed(2)}L`;
    return `₹${numericAmount.toLocaleString()}`;
  };

  const formatDate = (dateStr) => format(new Date(dateStr), "dd MMM yyyy");

  const duration = differenceInDays(
    new Date(workOrder.endDate),
    new Date(workOrder.startDate),
  );

  const toggleVersion = (version) => {
    setExpandedVersions((prev) =>
      prev.includes(version)
        ? prev.filter((v) => v !== version)
        : [...prev, version],
    );
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "completed":
        return "bg-sm-secondary/10 text-sm-secondary border-sm-secondary/30";
      case "paid":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "pending":
        return "bg-sm-accent/10 text-sm-accent border-sm-accent/30";
      case "in progress":
        return "bg-gray-500/10 text-gray-600 border-gray-500/30";
      case "draft":
        return "bg-gray-100 text-gray-600 border-gray-200";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  // Scope Item Handlers
  const handleEditScope = (item) => {
    setEditingScopeId(item.id);
    setScopeEdits({
      ...item,
      description: item.description ?? "",
      quantity: item.quantity,
      unit: item.unit,
      unitCost: item.unitCost,
      totalCost: item.totalCost,
    });
  };

  const handleScopeChange = (field, value) => {
    setScopeEdits((prev) => {
      const updates = { ...prev, [field]: value };
      if (field === "quantity" || field === "unitCost") {
        updates.totalCost =
          (parseFloat(updates.quantity) || 0) *
          (parseFloat(updates.unitCost) || 0);
      }
      return updates;
    });
  };

  const handleCancelScope = () => {
    setEditingScopeId(null);
    setScopeEdits({});
  };

  const handleSaveScope = async () => {
    try {
      setIsSaving(true);

      // 1. Update Scope Item
      const payload = {
        item: scopeEdits.item, // Preserving name if not editable, or adding if needed
        description: scopeEdits.description ?? "",
        quantity: parseFloat(scopeEdits.quantity),
        unit: scopeEdits.unit,
        unit_price: parseFloat(scopeEdits.unitCost),
        total_price: parseFloat(scopeEdits.totalCost),
      };

      await ContractorApi.updateScopeItem(editingScopeId, payload);

      // 2. Proportional Scaling for Milestones (if applicable)
      if (details.paymentStructure === "MILESTONE_BASED") {
        const oldTotal = details.value;
        const newTotal = details.scopeItems.reduce((sum, item) => {
          return (
            sum +
            (item.id === editingScopeId ? payload.total_price : item.totalCost)
          );
        }, 0);

        if (oldTotal !== newTotal) {
          // Calculate scaling factor or proportionally distribute
          // Strategy: Recalculate all UNPAID milestones to maintain their original *percentage* relative to the NEW total?
          // OR maintain amounts and adjust one?
          // User selected: "Proportional Scaling: Automatically recalculate all unpaid milestones to maintain their original percentage."
          // Implementation:
          // 1. Get current milestones.
          // 2. Identify unpaid ones.
          // 3. For each unpaid, new_amount = (old_percentage / 100) * newTotal.
          // Warning: This assumes paid milestones are fixed. If paid milestones exist, the "original percentage" concept gets tricky if total changes.
          // If I scale only unpaid, the sum might not equal newTotal if paid ones are fixed.
          // Let's assume we scale ALL milestones to keep percentages? No, paid ones can't change amount usually.
          // Better approach for "Proportional Scaling" with paid items:
          // 1. Calculate `paidTotal`.
          // 2. `remainingToAssign` = `newTotal` - `paidTotal`.
          // 3. Distribute `remainingToAssign` among unpaid milestones based on their relative weights.

          const unpaidMilestones = details.milestones.filter(
            (m) => m.status !== "Paid",
          );
          const paidMilestones = details.milestones.filter(
            (m) => m.status === "Paid",
          );
          const paidTotal = paidMilestones.reduce((sum, m) => sum + m.value, 0);
          const remainingTotal = newTotal - paidTotal;

          if (unpaidMilestones.length > 0 && remainingTotal > 0) {
            const unpaidSum = unpaidMilestones.reduce(
              (sum, m) => sum + m.value,
              0,
            );

            // Update each unpaid milestone
            // Update each unpaid milestone
            for (const milestone of unpaidMilestones) {
              let weight;
              if (unpaidSum === 0) {
                weight = 1 / unpaidMilestones.length;
              } else {
                weight = milestone.value / unpaidSum;
              }

              const newAmount = remainingTotal * weight;
              await ContractorApi.updateMilestone(milestone.id, {
                amount: newAmount,
              });
            }
          }
        }
      }

      setEditingScopeId(null);
      fetchDetails(); // Refresh data
    } catch (err) {
      console.error("Error saving scope item:", err);
      // Construct a User-friendly error message
      alert("Failed to save scope item. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Milestone Bulk Update Handler
  const handleSaveMilestones = async (updatedMilestones) => {
    try {
      setIsSaving(true);

      // Update each changed milestone
      for (const m of updatedMilestones) {
        // Only update if changed (optional optimization, but good for saving calls)
        const original = details.milestones.find((orig) => orig.id === m.id);
        if (
          original &&
          (original.value !== parseFloat(m.amount) ||
            original.name !== m.name ||
            original.description !== m.description)
        ) {
          await ContractorApi.updateMilestone(m.id, {
            name: m.name,
            description: m.description,
            amount: parseFloat(m.amount),
          });
        }
      }

      fetchDetails();
    } catch (err) {
      console.error("Error saving milestones:", err);
      alert("Failed to save milestones.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    setShowPreviewModal(true);
  };

  const handleUpgradeSuccess = () => {
    // Refresh page as requested
    fetchDetails();
  };

  const handleEditTermsClick = () => {
    setEditTermsData({
      terms: details.terms || "",
      paymentTerms: details.paymentTerms || "",
    });
    setShowEditTermsModal(true);
  };

  const handleSaveTerms = async () => {
    try {
      setIsSaving(true);
      const latestVersionId = details.versions[0].version_id;
      const res = await ContractorApi.editWorkOrderVersion(latestVersionId, {
        payment_terms: editTermsData.paymentTerms,
        terms_and_conditions: editTermsData.terms,
      });
      if (res.status) {
        setShowEditTermsModal(false);
        fetchDetails();
        if (onRefresh) onRefresh();
      } else {
        alert(res.message || "Failed to edit terms");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving terms");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditPaymentStructureClick = () => {
    if (details.paidAmount > 0) {
      alert("Work order cannot be edited after a payment has been done.");
      return;
    }
    setPaymentStructure(
      details.paymentStructure === "MILESTONE_BASED"
        ? "MILESTONE_BASED"
        : details.paymentStructure === "MONTHLY_RUNNING_BILL"
          ? "MONTHLY_RUNNING_BILL"
          : "LUMPSUM_CUSTOM",
    );
    if (details.milestones?.length > 0) {
      setStructureMilestones(
        details.milestones.map((m, i) => ({
          id: m.id || i,
          name: m.name || "",
          description: m.description || "",
          percentage: roundToTwo(m.percentage),
          amount: roundToTwo(
            m.value ?? (details.value * (Number(m.percentage) || 0)) / 100,
          ),
        })),
      );
    } else {
      setStructureMilestones([
        {
          id: Date.now(),
          name: "",
          description: "",
          percentage: 100,
          amount: roundToTwo(details.value),
        },
      ]);
    }

    setEditStartDate(
      details.startDate ? details.startDate.substring(0, 10) : "",
    );
    setEditEndDate(details.endDate ? details.endDate.substring(0, 10) : "");

    setShowEditPaymentModal(true);
  };

  const handleSavePaymentStructure = async () => {
    try {
      setIsSaving(true);
      const latestVersionId = details.versions[0].version_id;

      if (paymentStructure === "MILESTONE_BASED") {
        const totalAmount = structureMilestones.reduce(
          (acc, curr) => acc + (Number(curr.amount) || 0),
          0,
        );
        if (Math.abs(totalAmount - details.value) > 0.01) {
          alert(
            `Milestone amounts must equal ${formatCurrency(details.value)}`,
          );
          setIsSaving(false);
          return;
        }
      }

      const res = await ContractorApi.editWorkOrderVersion(latestVersionId, {
        payment_structure: paymentStructure,
        start_date: editStartDate || null,
        end_date: editEndDate || null,
      });

      if (!res.status) {
        throw new Error(res.message || "Failed to update payment structure");
      }

      if (paymentStructure === "MILESTONE_BASED") {
        const existingMilestonesToUpdate = [];
        const newMilestonesToCreate = [];

        structureMilestones.forEach((m) => {
          const isExisting = details.milestones?.find(
            (orig) => String(orig.id) === String(m.id),
          );
          if (isExisting) {
            existingMilestonesToUpdate.push(m);
          } else {
            newMilestonesToCreate.push({
              version: latestVersionId,
              name: m.name,
              description: m.description,
              amount: roundToTwo(m.amount),
            });
          }
        });

        for (const m of existingMilestonesToUpdate) {
          await ContractorApi.updateMilestone(m.id, {
            name: m.name,
            description: m.description,
            amount: roundToTwo(m.amount),
          });
        }

        if (newMilestonesToCreate.length > 0) {
          const res2 = await ContractorApi.createMilestones(latestVersionId, {
            milestones: newMilestonesToCreate,
          });
          if (!res2.status) {
            throw new Error(res2.message || "Failed to create new milestones");
          }
        }
      }

      setShowEditPaymentModal(false);
      fetchDetails();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
      alert("Error saving payment structure: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStructureMilestoneChange = (id, field, value) => {
    setStructureMilestones((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;

        const updates = { ...m, [field]: value };
        if (field === "amount") {
          updates.percentage = calculatePercentage(value, details.value);
        }
        return updates;
      }),
    );
  };
  const addStructureMilestone = () => {
    setStructureMilestones((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: "",
        description: "",
        percentage: 0,
        amount: "",
      },
    ]);
  };
  const removeStructureMilestone = (id) => {
    setStructureMilestones((prev) => prev.filter((m) => m.id !== id));
  };

  // Reusable Components replacements
  const Button = ({
    variant = "default",
    size = "default",
    className = "",
    children,
    ...props
  }) => {
    const baseStyles =
      "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
    const variants = {
      default: "bg-sm-primary text-white hover:bg-sm-primary/90",
      destructive:
        "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      outline:
        "border border-input bg-background hover:bg-sm-accent hover:text-sm-accent-foreground",
      secondary:
        "bg-sm-secondary text-sm-secondary-foreground hover:bg-sm-secondary/80",
      ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
      link: "text-sm-primary underline-offset-4 hover:underline",
    };
    const sizes = {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      icon: "h-10 w-10",
    };
    const sizeClasses = sizes[size];
    return (
      <button
        className={`${baseStyles} ${variants[variant]} ${sizeClasses} ${className}`}
        disabled={isSaving || props.disabled}
        {...props}
      >
        {children}
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] w-full bg-background rounded-xl border border-border">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sm-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] w-full bg-background rounded-xl border border-border p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-destructive font-semibold text-lg mb-2">Error</p>
        <p className="text-sm-secondary-foreground mb-4">{error}</p>
        <Button onClick={fetchDetails}>Try Again</Button>
      </div>
    );
  }

  if (!details) return null;

  const grandTotal = details.scopeItems.reduce(
    (sum, item) => sum + item.totalCost,
    0,
  );
  const structureMilestoneTotalAmount = structureMilestones.reduce(
    (acc, curr) => acc + (Number(curr.amount) || 0),
    0,
  );
  const isStructureMilestoneTotalMatched =
    Math.abs(structureMilestoneTotalAmount - details.value) <= 0.01;
  const currentVersion = details.versions.length
    ? Math.max(...details.versions.map((v) => v.version))
    : 1;

  return (
    <div className="space-y-6 p-4 pb-8">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-9 w-9"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-sm-primary">
                #{workOrder.id}
              </h1>
              <Badge
                className={`text-xs ${getStatusBadgeClass(details.approvalStatus)} border uppercase`}
              >
                {details.approvalStatus}
              </Badge>
              <Badge
                variant="outline"
                className="text-xs font-normal border-gray-300"
              >
                v{currentVersion}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {contractor.name} – {details.projectName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="h-9 text-sm gap-2"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
            Download Work Order
          </Button>
          <Button
            className="h-9 text-sm gap-2 bg-sm-primary hover:bg-sm-primary/90"
            onClick={() => setShowUpgradeDialog(true)}
          >
            <ArrowUpRight className="h-4 w-4" />
            Upgrade Contract
          </Button>
          <Button
            variant="destructive"
            className="h-9 text-sm gap-2 bg-red-500 text-white"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <UpgradeWorkOrderDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        asSidebar
        onClose={() => setShowUpgradeDialog(false)}
        workOrder={details}
        onUpgradeSuccess={handleUpgradeSuccess}
      />

      {typeof document !== "undefined" &&
        createPortal(
          <PDFPreviewModal
            open={showPreviewModal}
            onClose={() => setShowPreviewModal(false)}
            details={details}
            contractor={contractor}
            organization={organization}
          />,
          document.body,
        )}

      {typeof document !== "undefined" &&
        createPortal(
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={async () => {
              try {
                setIsDeletingWorkOrder(true);
                const response = await ContractorApi.deleteWorkOrder(details.id);
                if (response.status) {
                  setShowDeleteModal(false);
                  if (onDelete) {
                    onDelete(details.id);
                  } else {
                    onBack();
                  }
                } else {
                  alert(response.message || "Failed to delete work order");
                }
              } catch (err) {
                console.error("Error deleting work order:", err);
                alert("An error occurred while deleting the work order");
              } finally {
                setIsDeletingWorkOrder(false);
              }
            }}
            title="Delete Work Order"
            message="Are you sure you want to delete this work order, all related payments and attendance will be lost?"
            confirmText="Delete"
            isDeleting={isDeletingWorkOrder}
          />,
          document.body,
        )}

      {/* Section 1: Work Order Overview */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">
              Work Order Overview
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
              onClick={handleEditPaymentStructureClick}
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit Details
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User className="h-3.5 w-3.5" />
                Contractor
              </div>
              <p className="text-sm font-medium">{contractor.name}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                Project
              </div>
              <p className="text-sm font-medium">{details.projectName}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <IndianRupee className="h-3.5 w-3.5" />
                Payment Structure
              </div>
              <Badge className="text-xs font-normal bg-sm-secondary/10 text-sm-secondary border-0 capitalize">
                {details?.paymentStructure?.replace(/_/g, " ")?.toLowerCase()}
              </Badge>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <IndianRupee className="h-3.5 w-3.5" />
                Work Order Value
              </div>
              <p className="text-sm font-semibold text-sm-primary">
                {formatCurrency(details.value)}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                Amount Paid
              </div>
              <p className="text-sm font-semibold text-green-600">
                {formatCurrency(details.paidAmount)}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <AlertCircle className="h-3.5 w-3.5 text-yellow-600" />
                Due Amount
              </div>
              <p className="text-sm font-semibold text-yellow-600">
                {formatCurrency(details.value - details.paidAmount)}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Duration
              </div>
              <p className="text-sm font-medium">{details.duration} Days</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                Start Date
              </div>
              <p className="text-sm font-medium">
                {details.startDate ? formatDate(details.startDate) : "-"}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                End Date
              </div>
              <p className="text-sm font-medium">
                {details.endDate ? formatDate(details.endDate) : "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Scope & Items */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Scope & Items</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <table className="w-full caption-bottom text-sm">
              <thead className="[&_tr]:border-b">
                <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted hover:bg-transparent bg-muted/30">
                  <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                    Item
                  </th>
                  <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                    Description
                  </th>
                  <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                    Quantity
                  </th>
                  <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                    Unit
                  </th>
                  <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                    Unit Cost
                  </th>
                  <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                    Total
                  </th>
                  {/* <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">Attachments</th> */}
                  <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {details.scopeItems.map((item, index) => {
                  const isEditing = editingScopeId === item.id;
                  return (
                    <tr
                      key={item.id}
                      className={`border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted ${index % 2 === 0 ? "bg-background" : "bg-muted/20"}`}
                    >
                      <td className="p-4 align-middle py-2.5 text-sm font-medium w-[15%]">
                        {item.item}
                      </td>
                      <td className="p-4 align-middle py-2.5 text-sm text-muted-foreground w-[25%]">
                        {isEditing ? (
                          <Input
                            value={scopeEdits.description ?? ""}
                            onChange={(e) =>
                              handleScopeChange("description", e.target.value)
                            }
                          />
                        ) : (
                          item.description
                        )}
                      </td>
                      <td className="p-4 align-middle py-2.5 text-sm text-right w-[10%]">
                        {isEditing ? (
                          <Input
                            type="number"
                            className="text-right"
                            value={scopeEdits.quantity}
                            onChange={(e) =>
                              handleScopeChange("quantity", e.target.value)
                            }
                          />
                        ) : (
                          item.quantity.toLocaleString()
                        )}
                      </td>
                      <td className="p-4 align-middle py-2.5 text-sm w-[10%]">
                        {isEditing ? (
                          <Select
                            value={scopeEdits.unit}
                            onChange={(e) =>
                              handleScopeChange("unit", e.target.value)
                            }
                            options={UNIT_OPTIONS}
                          />
                        ) : (
                          item.unit
                        )}
                      </td>
                      <td className="p-4 align-middle py-2.5 text-sm text-right w-[10%]">
                        {isEditing ? (
                          <Input
                            type="number"
                            className="text-right"
                            value={scopeEdits.unitCost}
                            onChange={(e) =>
                              handleScopeChange("unitCost", e.target.value)
                            }
                          />
                        ) : (
                          `₹${item.unitCost.toLocaleString()}`
                        )}
                      </td>
                      <td className="p-4 align-middle py-2.5 text-sm text-right font-medium w-[10%]">
                        {isEditing
                          ? formatCurrency(scopeEdits.totalCost)
                          : formatCurrency(item.totalCost)}
                      </td>
                      {/* <td className="p-4 align-middle py-2.5 text-sm text-center w-[10%]">
                        <div className="flex justify-center">
                          {details.attachments?.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {details.attachments.map((url, idx) => {
                                const fileName = url.split('/').pop().split('?')[0];
                                return (
                                  <a
                                    key={idx}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm-primary hover:underline flex items-center gap-1 text-[10px]"
                                    title={fileName}
                                  >
                                    <Paperclip className="h-3 w-3" />
                                    <span className="truncate max-w-[80px]">File {idx + 1}</span>
                                  </a>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-[10px] italic">No files</span>
                          )}
                        </div>
                      </td> */}
                      <td className="p-4 align-middle py-2.5 text-sm text-right w-[10%]">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={handleSaveScope}
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={handleCancelScope}
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
                            onClick={() => handleEditScope(item)}
                          >
                            <Pencil className="h-4 w-4" />{" "}
                            {/* Using FileText as generic edit icon substitute if Pen not avail, or use text "Edit" */}
                            <span className="sr-only">Edit</span>
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-b transition-colors data-[state=selected]:bg-muted bg-muted/40 hover:bg-muted/40">
                  <td
                    colSpan={5}
                    className="p-4 align-middle py-2.5 text-sm font-semibold text-right"
                  >
                    Grand Total:
                  </td>
                  <td className="p-4 align-middle py-2.5 text-sm text-right font-bold text-sm-primary">
                    {formatCurrency(grandTotal)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
          {(details.scope_notes || details.scopeNotes) && (
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Scope Notes:</p>
              <p className="text-sm whitespace-pre-wrap">
                {details.scope_notes || details.scopeNotes}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Payment Structure */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base font-medium">
                Payment Structure
              </CardTitle>
              <Badge
                variant="secondary"
                className="text-xs bg-sm-secondary/10 text-sm-secondary border-0 capitalize"
              >
                {details.paymentStructure?.toLowerCase()?.replace("_", " ")}
              </Badge>
              {details.paymentStructure === "MILESTONE_BASED" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
                  onClick={() => setShowEditMilestonesModal(true)}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Milestone-Based */}
          {details.paymentStructure === "MILESTONE_BASED" &&
            details.milestones && (
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <table className="w-full caption-bottom text-sm">
                  <thead className="[&_tr]:border-b">
                    <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted hover:bg-transparent bg-muted/30">
                      <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                        Milestone
                      </th>
                      <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                        Description
                      </th>
                      <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">
                        %
                      </th>
                      <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                        Value
                      </th>
                      <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {details.milestones.map((milestone) => {
                      return (
                        <tr
                          key={milestone.id}
                          className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                        >
                          <td className="p-4 align-middle py-2.5 text-sm font-medium">
                            {milestone.name}
                          </td>
                          <td className="p-4 align-middle py-2.5 text-sm text-muted-foreground">
                            {milestone.description}
                          </td>
                          <td className="p-4 align-middle py-2.5 text-sm text-center">
                            {roundToTwo(milestone.percentage).toFixed(2) + "%"}
                          </td>
                          <td className="p-4 align-middle py-2.5 text-sm text-right font-medium">
                            {formatCurrency(milestone.value)}
                          </td>
                          <td className="p-4 align-middle py-2.5 text-center">
                            <Badge
                              className={`text-xs ${getStatusBadgeClass(milestone.status)} border capitalize`}
                            >
                              {milestone.status}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <EditMilestonesModal
                  open={showEditMilestonesModal}
                  onOpenChange={setShowEditMilestonesModal}
                  milestones={details.milestones}
                  totalOrderValue={details.value}
                  onSave={handleSaveMilestones}
                />
              </div>
            )}

          {details.paymentStructure === "MONTHLY_RUNNING_BILL" &&
            details.monthlyBills && (
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <table className="w-full caption-bottom text-sm">
                  <thead className="[&_tr]:border-b">
                    <tr className="border-b transition-colors hover:bg-transparent bg-muted/30">
                      <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                        Month
                      </th>
                      <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">
                        Progress
                      </th>
                      <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                        Amount
                      </th>
                      <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {details.monthlyBills.map((bill, index) => (
                      <tr
                        key={index}
                        className="border-b transition-colors hover:bg-muted/50"
                      >
                        <td className="p-4 align-middle py-2.5 text-sm font-medium">
                          {bill.month}
                        </td>
                        <td className="p-4 align-middle py-2.5 text-sm text-center">
                          {bill.progress}%
                        </td>
                        <td className="p-4 align-middle py-2.5 text-sm text-right font-medium">
                          {formatCurrency(bill.amount)}
                        </td>
                        <td className="p-4 align-middle py-2.5 text-center">
                          <Badge
                            className={`text-xs ${getStatusBadgeClass(bill.status)} border`}
                          >
                            {bill.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          {details.paymentStructure === "LUMPSUM_CUSTOM" && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Total Contract Value
                </p>
                <p className="text-lg font-semibold text-sm-primary">
                  {formatCurrency(details.value)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Payment Terms</p>
                <pre className="text-sm whitespace-pre-wrap font-outfit">
                  {details.paymentTerms || "-"}
                </pre>
              </div>
              {details.retentionPercent > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Retention</p>
                  <p className="text-sm font-medium">
                    {details.retentionPercent}%
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Terms, Acknowledgment, and Documents */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">
              Terms & Documents
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
              onClick={handleEditTermsClick}
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <span className="text-sm font-medium text-foreground whitespace-nowrap">
                Terms & Conditions:
              </span>
              <span className="text-sm text-muted-foreground">
                {details.terms}
              </span>
            </div>

            <div className="flex items-start gap-2">
              <span className="text-sm font-medium text-foreground whitespace-nowrap">
                Payment Terms:
              </span>
              <span className="text-sm text-muted-foreground">
                {details.paymentTerms || "No payment terms specified."}
              </span>
            </div>

            {/* <div className="flex items-start gap-2">
              <span className="text-sm font-medium text-foreground whitespace-nowrap">Scope Notes:</span>
              <span className="text-sm text-muted-foreground whitespace-pre-wrap">{details.scope_notes || "No scope notes specified."}</span>
            </div> */}
          </div>

          <Separator className="my-2" />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                Acknowledgment Date
              </p>
              <p className="text-sm font-medium">
                {details.acknowledgmentDate
                  ? formatDate(details.acknowledgmentDate)
                  : "-"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Approval Status</p>
              <Badge
                className={`text-xs ${getStatusBadgeClass(details.approvalStatus)} border uppercase`}
              >
                {details.approvalStatus}
              </Badge>
            </div>
          </div>
          {details.attachments?.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">
                  Attachments
                </p>
                <div className="flex flex-wrap gap-2">
                  {details.attachments.map((attachment, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-2"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      {attachment.name}
                      <Download className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Section 5: Version History */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-sm-secondary" />
            <CardTitle className="text-base font-medium">
              Version History
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {/* Latest version (pinned) — CHANGED: added start/end date row */}
          {details.versions.length > 0 && (
            <div className="bg-sm-secondary/5 border border-secondary/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className="bg-sm-secondary text-white text-xs border-transparent hover:bg-sm-secondary/80">
                    Latest
                  </Badge>
                  <span className="text-sm font-medium">v{currentVersion}</span>
                  <span className="text-xs text-muted-foreground">
                    Updated on {formatDate(details.versions[0].updatedOn)}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-sm-primary">
                    {formatCurrency(details.versions[0].totalValue)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    By {details.versions[0].updatedBy}
                  </span>
                </div>
              </div>
              {/* ADDED: start/end date row for latest version */}
              {(details.versions[0].startDate ||
                details.versions[0].endDate) && (
                <div className="flex items-center gap-4 mt-2 pt-2 border-t border-border/40">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Start:</span>
                    <span className="font-medium text-foreground">
                      {details.versions[0].startDate
                        ? formatDate(details.versions[0].startDate)
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>End:</span>
                    <span className="font-medium text-foreground">
                      {details.versions[0].endDate
                        ? formatDate(details.versions[0].endDate)
                        : "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Older versions — CHANGED: added inline start→end date in the collapsed header */}
          {details.versions.slice(1).map((version) => {
            const isOpen = expandedVersions.includes(version.version);
            return (
              <div
                key={version.version}
                className="border border-border/60 rounded-lg"
              >
                <button
                  onClick={() => toggleVersion(version.version)}
                  className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors rounded-lg bg-transparent border-0"
                >
                  <div className="flex items-center gap-3">
                    <Badge
                      variant="outline"
                      className="text-xs border-gray-300"
                    >
                      v{version.version}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Updated on {formatDate(version.updatedOn)}
                    </span>
                    {/* ADDED: inline date range */}
                    {(version.startDate || version.endDate) && (
                      <>
                        <span className="text-xs text-border select-none">
                          ·
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3 shrink-0" />
                          <span>
                            {version.startDate
                              ? formatDate(version.startDate)
                              : "—"}
                          </span>
                          <span className="mx-0.5">→</span>
                          <span>
                            {version.endDate
                              ? formatDate(version.endDate)
                              : "—"}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">
                      {formatCurrency(version.totalValue)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      By {version.updatedBy}
                    </span>
                    {isOpen ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-border/60 p-4 space-y-4 animate-in slide-in-from-top-1 fade-in-0 duration-200">
                    {version.scopeItems && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">
                          Scope Items
                        </p>
                        <div className="rounded-lg border border-border/40 overflow-hidden">
                          <table className="w-full caption-bottom text-sm">
                            <thead className="[&_tr]:border-b">
                              <tr className="border-b transition-colors hover:bg-transparent bg-muted/20">
                                <th className="h-8 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                                  Item
                                </th>
                                <th className="h-8 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                                  Description
                                </th>
                                <th className="h-8 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="[&_tr:last-child]:border-0">
                              {version.scopeItems.map((item) => (
                                <tr
                                  key={item.id}
                                  className="border-b transition-colors hover:bg-muted/50"
                                >
                                  <td className="p-4 align-middle py-2 text-xs">
                                    {item.item}
                                  </td>
                                  <td className="p-4 align-middle py-2 text-xs text-muted-foreground">
                                    {item.description}
                                  </td>
                                  <td className="p-4 align-middle py-2 text-xs text-right">
                                    {formatCurrency(item.totalCost)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {version.milestones && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">
                          Payment Milestones
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {version.milestones.map((m) => (
                            <Badge
                              key={m.id}
                              variant="outline"
                              className="text-xs border-gray-300"
                            >
                              {m.name}: {roundToTwo(m.percentage).toFixed(2)}%
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Version Terms */}
                    {(version.terms || version.paymentTerms) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {version.terms && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground font-medium">
                              Terms & Conditions
                            </p>
                            <p className="text-xs bg-muted/30 p-2 rounded whitespace-pre-wrap">
                              {version.terms}
                            </p>
                          </div>
                        )}
                        {version.paymentTerms && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground font-medium">
                              Payment Terms
                            </p>
                            <p className="text-xs bg-muted/30 p-2 rounded whitespace-pre-wrap">
                              {version.paymentTerms}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {details.versions.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <p className="text-sm">No previous versions available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Terms Modal */}
      {showEditTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto p-4">
          <div className="bg-background rounded-xl shadow-lg w-full max-w-md p-6 relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-8 w-8"
              onClick={() => setShowEditTermsModal(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold mb-6">
              Edit Terms & Documents
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">
                  Terms & Conditions
                </label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-1"
                  value={editTermsData.terms}
                  onChange={(e) =>
                    setEditTermsData({
                      ...editTermsData,
                      terms: e.target.value,
                    })
                  }
                  placeholder="Enter terms and conditions..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Payment Terms</label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-1"
                  value={editTermsData.paymentTerms}
                  onChange={(e) =>
                    setEditTermsData({
                      ...editTermsData,
                      paymentTerms: e.target.value,
                    })
                  }
                  placeholder="Enter payment terms..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEditTermsModal(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveTerms} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Payment Structure Modal */}
      {showEditPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto p-4 sm:p-6">
          <div className="bg-background rounded-xl shadow-lg w-full max-w-4xl p-6 relative flex flex-col max-h-full">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-8 w-8"
              onClick={() => setShowEditPaymentModal(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold mb-6 shrink-0">
              Edit Work Order Details
            </h2>

            <div className="flex-1 overflow-y-auto pr-1 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Start Date</label>
                  <Input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">End Date</label>
                  <Input
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-medium">
                  Payment Structure Type
                </label>
                <div className="flex flex-wrap gap-4 pt-1">
                  {[
                    { id: "MILESTONE_BASED", label: "Milestone Based" },
                    {
                      id: "MONTHLY_RUNNING_BILL",
                      label: "Monthly Running Bill",
                    },
                    { id: "LUMPSUM_CUSTOM", label: "Fixed Price (Custom)" },
                  ].map((type) => (
                    <div
                      key={type.id}
                      className={`flex items-center space-x-2 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                        paymentStructure === type.id
                          ? "border-primary bg-sm-primary/5"
                          : "border-border"
                      }`}
                      onClick={() => setPaymentStructure(type.id)}
                    >
                      <input
                        type="radio"
                        id={`payment-edit-${type.id}`}
                        name="paymentStructureEdit"
                        value={type.id}
                        checked={paymentStructure === type.id}
                        onChange={(e) => setPaymentStructure(e.target.value)}
                        className="aspect-square h-4 w-4 rounded-full border border-primary text-sm-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      />
                      <label
                        htmlFor={`payment-edit-${type.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {type.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {paymentStructure === "MILESTONE_BASED" && (
                <div className="space-y-4">
                  <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
                    <table className="w-full caption-bottom text-sm">
                      <thead className="[&_tr]:border-b bg-muted/40">
                        <tr className="border-b transition-colors hover:bg-transparent">
                          <th className="h-10 px-4 text-left font-medium text-muted-foreground w-1/4">
                            Milestone Name
                          </th>
                          <th className="h-10 px-4 text-left font-medium text-muted-foreground w-1/3">
                            Description
                          </th>
                          <th className="h-10 px-4 text-right font-medium text-muted-foreground w-20">
                            %
                          </th>
                          <th className="h-10 px-4 text-right font-medium text-muted-foreground w-32">
                            Amount
                          </th>
                          <th className="h-10 px-4 w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="[&_tr:last-child]:border-0">
                        {structureMilestones.map((milestone) => (
                          <tr key={milestone.id} className="border-b">
                            <td className="p-2">
                              <Input
                                value={milestone.name}
                                onChange={(e) =>
                                  handleStructureMilestoneChange(
                                    milestone.id,
                                    "name",
                                    e.target.value,
                                  )
                                }
                                placeholder="Name"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                value={milestone.description}
                                onChange={(e) =>
                                  handleStructureMilestoneChange(
                                    milestone.id,
                                    "description",
                                    e.target.value,
                                  )
                                }
                                placeholder="Description"
                              />
                            </td>
                            <td className="p-2 text-right text-sm text-muted-foreground">
                              {calculatePercentage(
                                milestone.amount,
                                details.value,
                              ).toFixed(2)}
                              %
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                value={milestone.amount}
                                onChange={(e) =>
                                  handleStructureMilestoneChange(
                                    milestone.id,
                                    "amount",
                                    e.target.value,
                                  )
                                }
                                min={0}
                                step="0.01"
                                className="text-right"
                              />
                            </td>
                            <td className="p-2 text-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() =>
                                  removeStructureMilestone(milestone.id)
                                }
                                disabled={structureMilestones.length <= 1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      onClick={addStructureMilestone}
                      className="text-sm-primary gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Milestone
                    </Button>
                    <div
                      className={`text-sm font-medium ${
                        isStructureMilestoneTotalMatched
                          ? "text-green-600"
                          : "text-destructive"
                      }`}
                    >
                      Total:{" "}
                      {formatCurrency(structureMilestoneTotalAmount)}
                      {!isStructureMilestoneTotalMatched &&
                        ` (must equal ${formatCurrency(details.value)})`}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 pt-6 mt-4 border-t flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowEditPaymentModal(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSavePaymentStructure} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
